"use client";

import { useState, useEffect } from "react";
import { BottomSheet } from "react-spring-bottom-sheet";
import "react-spring-bottom-sheet/dist/style.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { CloseIcon } from "./CloseIcon";

interface FiltersSheetProps {
  open: boolean;
  onDismiss: () => void;
  onApplyOptions: (options: string[]) => void;
  availableOptions?: string[];
  selectedOptions?: string[];
  title?: string;
  searchPlaceholder?: string;
}

export function FiltersSheet({
  open,
  onDismiss,
  onApplyOptions,
  availableOptions = [],
  selectedOptions = [],
  title = "Фильтры",
  searchPlaceholder = "Поиск...",
}: FiltersSheetProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSelectedOptions, setTempSelectedOptions] = useState<string[]>([]);

  // Filter options based on search query with safe array handling
  const filteredOptions = (availableOptions || []).filter((option) =>
    option?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleOptionToggle = (option: string) => {
    setTempSelectedOptions((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option],
    );
  };

  const handleRemoveOption = (optionToRemove: string) => {
    setTempSelectedOptions((prev) =>
      prev.filter((option) => option !== optionToRemove),
    );
  };

  const handleApplyFilters = () => {
    onApplyOptions(tempSelectedOptions);
    onDismiss();
  };

  const handleDismiss = () => {
    // Reset to original selected options if user cancels
    setTempSelectedOptions(selectedOptions || []);
    setSearchQuery("");
    onDismiss();
  };

  // Initialize tempSelectedOptions when sheet opens
  useEffect(() => {
    if (open) {
      setTempSelectedOptions(selectedOptions || []);
    }
  }, [open, selectedOptions]);

  return (
    <BottomSheet
      open={open}
      onDismiss={handleDismiss}
      snapPoints={({ maxHeight }) => [maxHeight * 0.9]}
      scrollLocking={true}
      blocking={true}
      expandOnContentDrag={true}
      className="z-50"
      header={
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-black">{title}</h2>

          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <Input
              type="search"
              placeholder={searchPlaceholder}
              className="pl-10 bg-gray-100 border-gray-100 text-black"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Selected Options Pills - Fixed Height */}
          <div className="min-h-[40px]">
            {tempSelectedOptions.length > 0 ? (
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {tempSelectedOptions.map((option) => (
                  <div
                    key={option}
                    className="flex items-center gap-1 bg-[#D33F44] text-white px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0"
                  >
                    <span>{option}</span>
                    <button
                      onClick={() => handleRemoveOption(option)}
                      className="ml-1 hover:bg-[#B8353A] rounded-full p-0.5 transition-colors"
                    >
                      <CloseIcon />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500">Ничего не выбрано </div>
            )}
          </div>
        </div>
      }
      footer={
        <div className="pv-4 bg-white">
          <Button
            onClick={handleApplyFilters}
            className="w-full h-11 bg-[#D33F44] hover:bg-[#B8353A] text-white"
            disabled={(availableOptions || []).length === 0}
          >
            Применить
          </Button>
        </div>
      }
    >
      {filteredOptions.length > 0 ? (
        <div>
          {filteredOptions.map((option) => (
            <div
              key={option}
              className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
              onClick={() => handleOptionToggle(option)}
            >
              <Checkbox
                id={option}
                checked={tempSelectedOptions.includes(option)}
                className="data-[state=checked]:bg-[#D33F44] data-[state=checked]:border-[#D33F44]"
              />
              <label
                htmlFor={option}
                className="text-sm font-medium text-gray-900 cursor-pointer flex-1"
              >
                {option}
              </label>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <p className="text-sm">
            {searchQuery ? "Ничего не найдено" : "Нет доступных опций"}
          </p>
        </div>
      )}
    </BottomSheet>
  );
}
